import React, { useEffect, useMemo, useState } from 'react';
import { Card, Input, Select, Space, Typography, message, Row, Col, Statistic, Progress } from 'antd';
import StandardTable from '../../../components/common/StandardTable';
import ActivityDetailsShow from '../../../components/modals/ActivityDetailsShow';
import { useGetTkfTaskActivityCodes, useGetTkfTasksTotalCosts } from '../../../hooks/genericHooks';
import type { TkfTaskTotalCostRow, VwTkfTaskActivityCode } from '../../../types';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtMoney = (v: any): string => {
  const n = toNum(v);
  if (n == null) return '';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

const tokenizeAnd = (q: string): string[] => {
  const s = String(q || '').trim().toLowerCase();
  if (!s) return [];
  return s
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);
};

const normalizeRows = <T,>(resp: any): T[] => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp?.data)) return resp.data as T[];
  if (Array.isArray(resp?.rows)) return resp.rows as T[];
  if (Array.isArray(resp?.recordset)) return resp.recordset as T[];
  return [];
};

const ToPlnSchProgressMonitor: React.FC = () => {
  const [activityNameLike, setActivityNameLike] = useState('');
  const [taskCodeLike, setTaskCodeLike] = useState('');
  const [selectedActvCodeTypeId, setSelectedActvCodeTypeId] = useState<number | null>(null);
  const [selectedActvCodeId, setSelectedActvCodeId] = useState<number | null>(null);
  const [detailTaskCode, setDetailTaskCode] = useState<string | null>(null);

  const { data: resp, isLoading, error } = useGetTkfTasksTotalCosts();
  const { data: taskActvCodesResp, isLoading: taskActvCodesLoading, error: taskActvCodesError } = useGetTkfTaskActivityCodes();

  const rows: TkfTaskTotalCostRow[] = useMemo(() => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp as any;
    if (Array.isArray((resp as any)?.data)) return (resp as any).data as any;
    if (Array.isArray((resp as any)?.rows)) return (resp as any).rows as any;
    if (Array.isArray((resp as any)?.recordset)) return (resp as any).recordset as any;
    return [];
  }, [resp]);

  useEffect(() => {
    if (error) {
      message.error((error as any)?.message || 'Failed to load progress monitor rows');
    }
  }, [error]);

  useEffect(() => {
    if (taskActvCodesError) {
      message.error((taskActvCodesError as any)?.message || 'Failed to load activity codes');
    }
  }, [taskActvCodesError]);

  useEffect(() => {
    setSelectedActvCodeId(null);
  }, [selectedActvCodeTypeId]);

  const allTaskActvCodes = useMemo(
    () => normalizeRows<VwTkfTaskActivityCode>(taskActvCodesResp),
    [taskActvCodesResp]
  );

  const rowMatchesText = (r: TkfTaskTotalCostRow) => {
    const nameTokens = tokenizeAnd(activityNameLike);
    const codeTokens = tokenizeAnd(taskCodeLike);

    if (nameTokens.length) {
      const hay = String(r.task_name ?? '').toLowerCase();
      if (!nameTokens.every((tok) => hay.includes(tok))) return false;
    }

    if (codeTokens.length) {
      const hay = String(r.task_code ?? '').toLowerCase();
      if (!codeTokens.every((tok) => hay.includes(tok))) return false;
    }

    return true;
  };

  const textFilteredRows = useMemo(() => {
    return rows.filter((r) => rowMatchesText(r));
  }, [rows, activityNameLike, taskCodeLike]);

  const candidateTaskIdSet = useMemo(() => {
    const s = new Set<string>();
    textFilteredRows.forEach((r) => {
      if (r.task_id != null) s.add(String(r.task_id));
    });
    return s;
  }, [textFilteredRows]);

  const activityCodeTypeOptions = useMemo(() => {
    const m = new Map<number, string>();
    allTaskActvCodes.forEach((r) => {
      const tid = r.task_id != null ? String(r.task_id) : '';
      if (!tid || !candidateTaskIdSet.has(tid)) return;
      const id = r.actv_code_type_id != null ? Number(r.actv_code_type_id) : null;
      if (id == null || !Number.isFinite(id)) return;
      const label = String(r.actv_code_type ?? '').trim();
      if (!label) return;
      if (!m.has(id)) m.set(id, label);
    });
    return Array.from(m.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [allTaskActvCodes, candidateTaskIdSet]);

  const activityCodeValueOptions = useMemo(() => {
    if (selectedActvCodeTypeId == null) return [];
    const m = new Map<number, string>();
    allTaskActvCodes.forEach((r) => {
      const tid = r.task_id != null ? String(r.task_id) : '';
      if (!tid || !candidateTaskIdSet.has(tid)) return;
      if (Number(r.actv_code_type_id) !== Number(selectedActvCodeTypeId)) return;
      const id = r.actv_code_id != null ? Number(r.actv_code_id) : null;
      if (id == null || !Number.isFinite(id)) return;
      const code = String(r.actv_code ?? '').trim();
      if (!code) return;
      const label = `${code}${r.actv_name ? ` - ${String(r.actv_name)}` : ''}`;
      if (!m.has(id)) m.set(id, label);
    });
    return Array.from(m.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [allTaskActvCodes, candidateTaskIdSet, selectedActvCodeTypeId]);

  const allowedTaskIdSet = useMemo(() => {
    if (selectedActvCodeTypeId == null || selectedActvCodeId == null) return null;
    const s = new Set<string>();
    allTaskActvCodes.forEach((r) => {
      if (Number(r.actv_code_type_id) !== Number(selectedActvCodeTypeId)) return;
      if (Number(r.actv_code_id) !== Number(selectedActvCodeId)) return;
      if (r.task_id != null) s.add(String(r.task_id));
    });
    return s;
  }, [allTaskActvCodes, selectedActvCodeTypeId, selectedActvCodeId]);

  const filteredRows = useMemo(() => {
    if (!allowedTaskIdSet) return textFilteredRows;
    return textFilteredRows.filter((r) => r.task_id != null && allowedTaskIdSet.has(String(r.task_id)));
  }, [textFilteredRows, allowedTaskIdSet]);

  const tableRows = useMemo(() => {
    return filteredRows.map((r) => {
      const budget = toNum((r as any)?.budgeted_total_cost) ?? 0;
      const actual = toNum((r as any)?.actual_total_cost) ?? 0;
      const actualCapped = toNum((r as any)?.actual_capped_total_cost) ?? Math.min(actual, budget);
      const cac = toNum((r as any)?.cost_at_completion) ?? 0;
      const progressRatio = toNum((r as any)?.progress_ratio);
      const earnedRatio = progressRatio != null ? clamp01(progressRatio) : budget > 0 ? clamp01(actualCapped / budget) : 0;

      return {
        ...r,
        _budgeted_total_cost: budget,
        _actual_total_cost: actual,
        _actual_capped_total_cost: actualCapped,
        _cost_at_completion: cac,
        _earned_ratio: earnedRatio,
      };
    });
  }, [filteredRows]);

  const totals = useMemo(() => {
    const acc = {
      budgeted: 0,
      actual: 0,
      actualCapped: 0,
      cac: 0,
      progressPct: 0,
    };
    for (const r of tableRows || []) {
      acc.budgeted += toNum((r as any)?._budgeted_total_cost) ?? 0;
      acc.actual += toNum((r as any)?._actual_total_cost) ?? 0;
      acc.actualCapped += toNum((r as any)?._actual_capped_total_cost) ?? 0;
      acc.cac += toNum((r as any)?._cost_at_completion) ?? 0;
    }
    acc.progressPct = acc.budgeted > 0 ? clamp01(acc.actualCapped / acc.budgeted) : 0;
    return acc;
  }, [tableRows]);

  const tableRowsWithWeight = useMemo(() => {
    const totalBudget = toNum(totals.budgeted) ?? 0;
    return (tableRows || []).map((r: any) => {
      const b = toNum(r?._budgeted_total_cost) ?? 0;
      const w = totalBudget > 0 ? clamp01(b / totalBudget) : 0;
      return { ...r, _weightage_ratio: w };
    });
  }, [tableRows, totals.budgeted]);

  const columns = useMemo(() => {
    return [
      { title: 'Activity', dataIndex: 'task_code', key: 'task_code', width: 140 },
      { title: 'Activity Name', dataIndex: 'task_name', key: 'task_name', width: 260 },
      {
        title: 'Budgeted Cost',
        dataIndex: '_budgeted_total_cost',
        key: '_budgeted_total_cost',
        width: 140,
        align: 'right' as const,
        render: (v: any) => fmtMoney(v),
        sorter: (a: any, b: any) => (toNum(a?._budgeted_total_cost) ?? 0) - (toNum(b?._budgeted_total_cost) ?? 0),
      },
      {
        title: 'Weightage %',
        dataIndex: '_weightage_ratio',
        key: '_weightage_ratio',
        width: 120,
        align: 'right' as const,
        render: (v: any) => {
          const n = toNum(v) ?? 0;
          return `${(n * 100).toFixed(4)}%`;
        },
        sorter: (a: any, b: any) => (toNum(a?._weightage_ratio) ?? 0) - (toNum(b?._weightage_ratio) ?? 0),
      },
      {
        title: 'Actual Cost',
        dataIndex: '_actual_total_cost',
        key: '_actual_total_cost',
        width: 140,
        align: 'right' as const,
        render: (v: any) => fmtMoney(v),
        sorter: (a: any, b: any) => (toNum(a?._actual_total_cost) ?? 0) - (toNum(b?._actual_total_cost) ?? 0),
      },
      {
        title: 'Cost at Completion',
        dataIndex: '_cost_at_completion',
        key: '_cost_at_completion',
        width: 160,
        align: 'right' as const,
        render: (v: any) => fmtMoney(v),
        sorter: (a: any, b: any) => (toNum(a?._cost_at_completion) ?? 0) - (toNum(b?._cost_at_completion) ?? 0),
      },
      {
        title: 'Achieved % (Actual/Budget)',
        dataIndex: '_earned_ratio',
        key: '_earned_ratio',
        width: 170,
        align: 'right' as const,
        render: (v: any) => {
          const n = toNum(v) ?? 0;
          return `${Math.round(n * 100)}%`;
        },
        sorter: (a: any, b: any) => (toNum(a?._earned_ratio) ?? 0) - (toNum(b?._earned_ratio) ?? 0),
      },
    ];
  }, []);

  return (
    <Card style={{ margin: 16, padding: 0 }} styles={{ body: { padding: 0 } }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          padding: '16px 24px 8px 24px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Schedule Progress Monitor
        </Title>
        <Text type="secondary">Cost-based progress monitoring</Text>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '8px 24px 8px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Space wrap size={12}>
          <Input
            value={activityNameLike}
            onChange={(e) => setActivityNameLike(e.target.value)}
            placeholder="Activity Name contains"
            allowClear
            style={{ width: 260 }}
            disabled={isLoading}
          />
          <Input
            value={taskCodeLike}
            onChange={(e) => setTaskCodeLike(e.target.value)}
            placeholder="Task Code contains"
            allowClear
            style={{ width: 220 }}
            disabled={isLoading}
          />
          <Select
            placeholder="Activity Code Type"
            value={selectedActvCodeTypeId ?? undefined}
            allowClear
            loading={taskActvCodesLoading}
            onChange={(v) => setSelectedActvCodeTypeId(v != null ? Number(v) : null)}
            options={activityCodeTypeOptions}
            style={{ width: 200 }}
            showSearch
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            disabled={isLoading}
          />
          <Select
            placeholder="Activity Code Value"
            value={selectedActvCodeId ?? undefined}
            allowClear
            loading={taskActvCodesLoading}
            onChange={(v) => setSelectedActvCodeId(v != null ? Number(v) : null)}
            options={activityCodeValueOptions}
            disabled={isLoading || selectedActvCodeTypeId == null}
            style={{ width: 220 }}
            showSearch
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
          <Button
            onClick={() => {
              setActivityNameLike('');
              setTaskCodeLike('');
              setSelectedActvCodeTypeId(null);
              setSelectedActvCodeId(null);
            }}
            disabled={isLoading}
          >
            Reset
          </Button>
        </Space>
      </div>

      <div style={{ padding: '8px 24px 16px 24px' }}>
        <div style={{ marginBottom: 12 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={12} md={4}>
              <Statistic title="Budgeted" value={totals.budgeted} precision={2} formatter={(v) => fmtMoney(v)} />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Statistic title="Actual (Uncapped)" value={totals.actual} precision={2} formatter={(v) => fmtMoney(v)} />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Statistic title="Actual (Capped)" value={totals.actualCapped} precision={2} formatter={(v) => fmtMoney(v)} />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Statistic title="Cost at Completion" value={totals.cac} precision={2} formatter={(v) => fmtMoney(v)} />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <div>
                <Text type="secondary">Progress</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                  <Progress
                    percent={Number(((toNum(totals.progressPct) ?? 0) * 100).toFixed(2))}
                    size="small"
                    style={{ flex: 1, margin: 0 }}
                  />
                  <Text type="secondary">Rows: {tableRows.length}</Text>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <StandardTable
          columns={columns as any}
          dataSource={tableRowsWithWeight}
          rowKey={(r: any) => String(r?.task_id ?? r?.task_code ?? Math.random())}
          loading={isLoading}
          enableExport={true}
          exportFileName="schedule_progress_monitor"
          scroll={{ x: 'max-content' }}
          onRow={(record: any) => ({
            onDoubleClick: () => {
              const code = String(record?.task_code ?? '').trim();
              if (code) setDetailTaskCode(code);
            },
            style: { cursor: 'pointer' },
          })}
        />
      </div>
      <ActivityDetailsShow
        open={detailTaskCode != null}
        taskCode={detailTaskCode}
        onClose={() => setDetailTaskCode(null)}
      />
    </Card>
  );
};

export default ToPlnSchProgressMonitor;
